import { Icon } from '@iconify/react';
import { UserCustomStatusChanger } from '@modules/user-status/UserCustomStatusChanger';
import { getEffectiveUserStatus } from '@modules/user/utils/getEffectiveUserStatus';
import {
  Box,
  Divider,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Popover,
  Stack,
  Tooltip,
  Typography,
  styled,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  bindPopper,
  bindTrigger,
  usePopupState,
} from 'material-ui-popup-state/hooks';
import { useNavigate } from 'react-router-dom';
import { routes } from 'src/Router';
import { useAuthContext } from 'src/auth/useAuthContext';
import { IconButtonAnimate } from 'src/components/animate';
import { CPAvatar } from 'src/components/base/CPAvatar';
import { StyledArrow } from 'src/components/menu-popover/styles';
import { useThemeSettingsContext } from 'src/components/settings/ThemeSettingsContext';
import { useSnackbar } from 'src/components/snackbar';
import { useLocales } from 'src/locales';
import { useSelector } from 'src/redux/store';

export function AccountPopover() {
  const { enqueueSnackbar } = useSnackbar();
  const { translate } = useLocales();
  const navigate = useNavigate();

  const { workspace } = useSelector((state) => state.workspace);

  const { user, logout } = useAuthContext();

  const { setOpen: setThemeSettingsOpen } = useThemeSettingsContext();

  const popupState = usePopupState({
    variant: 'popover',
    popupId: 'accountPopover',
  });

  const handleLogout = async () => {
    try {
      await logout();
      window.location.replace(routes.auth.login);
      popupState.close();
    } catch (error) {
      console.error(error);
      enqueueSnackbar(translate('header.profile_dropdown.logout_error_msg'), {
        variant: 'error',
      });
    }
  };

  const handleClickItem = (path: string) => {
    popupState.close();
    navigate(path);
  };

  const handleOpenThemeSettings = () => {
    popupState.close();
    setThemeSettingsOpen(true);
  };

  const OPTIONS = [
    {
      label: translate('header.profile_dropdown.profile_item'),
      linkTo: routes.settings.user.account,
      icon: <Icon icon="tabler:user" />,
    },
    // {
    //   label: translate('header.profile_dropdown.notification_item'),
    //   linkTo: routes.settings.user.notifications,
    //   icon: <Icon icon="tabler:bell" />,
    // },
    {
      label: translate('header.profile_dropdown.language_item'),
      linkTo: routes.settings.user.language,
      icon: <Icon icon="tabler:world" />,
    },
  ];

  return (
    <>
      <Tooltip
        placement="bottom"
        arrow
        title={translate('shared.labels.user_profile')}
      >
        <StyledIconButtonAnimate {...bindTrigger(popupState)}>
          <CPAvatar
            picture={user?.picture}
            status={getEffectiveUserStatus(user)}
            name={user?.name || ''}
            size="xSmall"
          />
        </StyledIconButtonAnimate>
      </Tooltip>
      <StyledPopover
        {...bindPopper(popupState)}
        onClose={() => popupState.close()}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <StyledArrow arrow="top-right" />
        <StyledUserContainer>
          <Stack direction="row" alignItems="center">
            <CPAvatar
              picture={user?.picture}
              status={user?.customStatus?.status}
              name={user?.name || ''}
            />
            <Box sx={{ ml: 2 }}>
              <Typography variant="subtitle2">{user?.name}</Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {workspace?.name || user?.workspace.name}
              </Typography>
            </Box>
          </Stack>
        </StyledUserContainer>

        <Divider sx={{ borderStyle: 'solid', mb: 1 }} />

        <Stack overflow="auto" maxHeight={1}>
          <UserCustomStatusChanger />

          <Divider sx={{ borderStyle: 'solid', mb: 1 }} />

          <Stack>
            {OPTIONS.map((option) => (
              <MenuItem
                key={option.label}
                onClick={() => handleClickItem(option.linkTo)}
              >
                <ListItemIcon>{option.icon}</ListItemIcon>
                <ListItemText>{option.label}</ListItemText>
              </MenuItem>
            ))}
            <MenuItem onClick={handleOpenThemeSettings}>
              <ListItemIcon>
                <Icon icon="material-symbols:palette-outline" />
              </ListItemIcon>
              <ListItemText>
                {translate('header.profile_dropdown.theme_item')}
              </ListItemText>
            </MenuItem>

            <Divider sx={{ borderStyle: 'solid' }} />

            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <Icon icon="ic:round-log-out" color="error" />
              </ListItemIcon>
              <ListItemText
                primary={`${translate('header.profile_dropdown.logout_item')}`}
                primaryTypographyProps={{ color: 'error' }}
              />
            </MenuItem>
          </Stack>
        </Stack>
      </StyledPopover>
    </>
  );
}

const StyledIconButtonAnimate = styled(IconButtonAnimate)(({ theme }) => ({
  padding: 0,
  marginLeft: theme.spacing(1),
  '& .opened': {
    '&:before': {
      content: "''",
      width: '100%',
      height: '100%',
      borderRadius: '50%',
      position: 'absolute',
      backgroundColor: alpha(theme.palette.grey[900], 0.8),
      zIndex: 1,
    },
  },
}));

const StyledPopover = styled(Popover)(({ theme }) => ({
  '& .MuiPaper-root': {
    right: 8,
    left: 'unset !important',
    width: 320,
    padding: theme.spacing(0.5),
    overflow: 'inherit',

    '& .MuiMenuItem-root:not(:hover):not(.Mui-selected)': {
      backgroundColor: 'transparent',
    },
  },
}));

const StyledUserContainer = styled('div')(({ theme }) => ({
  padding: theme.spacing(1.5, 1),
}));
